import { AlertTriangle, Check, Maximize2, RotateCw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Kop, Laden } from './ui'
import {
  alsDocument, draai90, keurScan, naarBinnen, rechtTrekken, standaardHoeken, vindHoeken,
  type Beeld, type Hoeken, type Keuring,
} from '../lib/documentscan'

/* ===========================================================================
 *  Van foto naar scan: hoeken, rechttrekken, nakijken
 *
 *  Na de foto twee stappen, zoals de scanner van de iPhone:
 *
 *    Bijsnijden  de hoeken van het vel staan er al op (vindHoeken); klopt
 *                het niet, versleep ze met je vinger
 *    Resultaat   het rechtgetrokken vel, als document (wit papier, zwarte
 *                tekst) of in kleur -- en als het waarschijnlijk niet te
 *                lezen is, staat dat erbij, met "Opnieuw maken" vooraan
 *
 *  Het rekenwerk staat in lib/documentscan.ts; hier alleen tekenen en
 *  vingers.
 * =========================================================================== */

/** Hoe groot de foto in het geheugen mag zijn. Groter wint niets aan leesbaarheid. */
const BRON_MAX = 3000
/** Op welke maat het vel gezocht wordt. Klein is snel, en de hoeken worden toch versleept. */
const ZOEK_MAX = 520

export default function Scanner({ bestand, nummer, klaar, opnieuw }: {
  bestand: File
  nummer: number
  klaar: (scan: File) => void
  opnieuw: () => void
}) {
  const [bron, setBron] = useState<Beeld | null>(null)
  const [url, setUrl] = useState<string | null>(null)
  const [hoeken, setHoeken] = useState<Hoeken | null>(null)
  const [gevonden, setGevonden] = useState(true)
  const [fout, setFout] = useState<string | null>(null)
  const [stap, setStap] = useState<'hoeken' | 'rekenen' | 'resultaat'>('hoeken')
  const [scan, setScan] = useState<{ kleur: Beeld; document: Beeld; keuring: Keuring } | null>(null)
  const [soort, setSoort] = useState<'document' | 'kleur'>('document')

  /* De foto inlezen: als <img>, zodat de draaiing uit de camera (EXIF) al goed staat. */
  useEffect(() => {
    /* `weg`: React draait dit effect in de ontwikkelstand twee keer, en de
       opruiming van de eerste ronde trekt het adres in terwijl die foto nog
       laadt. Zonder dit gaf de eerste ronde "kon niet worden geopend" boven
       een foto die gewoon in beeld stond. */
    let weg = false
    const u = URL.createObjectURL(bestand)
    setUrl(u)
    const img = new Image()
    img.onload = () => {
      if (weg) return
      try {
        const f = Math.min(1, BRON_MAX / Math.max(img.naturalWidth, img.naturalHeight))
        const b = tekenOp(img, Math.round(img.naturalWidth * f), Math.round(img.naturalHeight * f))
        const z = Math.min(1, ZOEK_MAX / Math.max(b.width, b.height))
        const klein = tekenOp(img, Math.round(b.width * z), Math.round(b.height * z))
        const h = vindHoeken(klein)
        setGevonden(!!h)
        setHoeken(h
          ? naarBinnen(h.map((p) => ({ x: p.x / z, y: p.y / z })) as Hoeken)
          : standaardHoeken(b.width, b.height))
        setBron(b)
      } catch (e) {
        setFout('Deze foto kon niet worden ingelezen. ' + (e instanceof Error ? e.message : ''))
      }
    }
    img.onerror = () => !weg && setFout('Deze foto kon niet worden geopend. Een HEIC-foto? Kies in de camera "Meest compatibel", of maak hem hier opnieuw.')
    img.src = u
    return () => { weg = true; URL.revokeObjectURL(u) }
  }, [bestand])

  function maakScan() {
    if (!bron || !hoeken) return
    setStap('rekenen')
    /* Even wachten, zodat "Bezig" eerst op het scherm staat: het rekenen
       houdt de pagina een halve seconde bezig. */
    window.setTimeout(() => {
      try {
        const kleur = rechtTrekken(bron, hoeken, 2400)
        setScan({ kleur, document: alsDocument(kleur), keuring: keurScan(kleur) })
        setStap('resultaat')
      } catch (e) {
        setFout('Rechttrekken lukte niet: ' + (e instanceof Error ? e.message : String(e)) + ' Zet de hoeken verder uit elkaar.')
        setStap('hoeken')
      }
    }, 40)
  }

  function draai() {
    if (!scan) return
    setScan({ ...scan, kleur: draai90(scan.kleur), document: draai90(scan.document) })
  }

  async function gebruik() {
    if (!scan) return
    const b = soort === 'document' ? scan.document : scan.kleur
    const doek = document.createElement('canvas')
    doek.width = b.width
    doek.height = b.height
    doek.getContext('2d')!.putImageData(new ImageData(b.data, b.width, b.height), 0, 0)
    const blob = await new Promise<Blob | null>((r) => doek.toBlob(r, 'image/jpeg', soort === 'document' ? 0.8 : 0.82))
    if (!blob) { setFout('De scan kon niet worden opgeslagen.'); return }
    klaar(new File([blob], `scan-${nummer}.jpg`, { type: 'image/jpeg', lastModified: Date.now() }))
  }

  /* ---------------- het scherm ---------------- */

  if (stap === 'resultaat' && scan) {
    const problemen = scan.keuring.problemen
    return (
      <div className="scanner">
        <Kop titel={`Bladzijde ${nummer}`} terugLabel="Hoeken" opTerug={() => setStap('hoeken')} />
        <div className="schuif-balk">
          <div className="schuif" role="tablist">
            <button role="tab" aria-selected={soort === 'document'} className={soort === 'document' ? 'aan' : ''} onClick={() => setSoort('document')}>Document</button>
            <button role="tab" aria-selected={soort === 'kleur'} className={soort === 'kleur' ? 'aan' : ''} onClick={() => setSoort('kleur')}>Kleur</button>
          </div>
        </div>

        {problemen.length > 0 && (
          <div className="let let-let-op scan-let" role="alert">
            <AlertTriangle size={18} aria-hidden="true" />
            <div>
              <strong>Waarschijnlijk niet goed te lezen</strong>
              {problemen.map((p) => <div key={p}>{p}</div>)}
            </div>
          </div>
        )}

        <div className="scan-voorbeeld">
          <Voorbeeld beeld={soort === 'document' ? scan.document : scan.kleur} />
        </div>

        <div className="scan-balk">
          <button className="knop lucht" onClick={opnieuw}>Opnieuw maken</button>
          <button className="rond-knop klein-rond" onClick={draai} aria-label="Draaien"><RotateCw size={20} /></button>
          <button className={`knop ${problemen.length ? 'grijs-knop' : 'blauw'}`} onClick={() => void gebruik()}>
            <Check size={18} /> {problemen.length ? 'Toch gebruiken' : 'Gebruiken'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="scanner">
      <Kop titel={`Bladzijde ${nummer}`} terugLabel="Annuleren" opTerug={opnieuw} />
      {fout && <div className="foutvak">{fout}</div>}
      {!bron && !fout && <Laden tekst="Het vel wordt gezocht…" />}
      {bron && hoeken && url && (
        <>
          <p className="scan-uitleg">
            {gevonden
              ? 'Klopt het kader om het vel? Versleep anders de hoeken.'
              : 'Het vel was niet te vinden. Sleep de vier hoeken naar de hoeken van het papier.'}
          </p>
          <HoekenKiezer url={url} bron={bron} hoeken={hoeken} zet={setHoeken} />
          <div className="scan-balk">
            <button className="knop lucht" onClick={opnieuw}>Opnieuw</button>
            <button className="rond-knop klein-rond" aria-label="Hele foto gebruiken"
              onClick={() => setHoeken([{ x: 0, y: 0 }, { x: bron.width - 1, y: 0 }, { x: bron.width - 1, y: bron.height - 1 }, { x: 0, y: bron.height - 1 }])}>
              <Maximize2 size={20} />
            </button>
            <button className="knop blauw" onClick={maakScan} disabled={stap === 'rekenen'}>
              {stap === 'rekenen' ? 'Bezig…' : 'Scannen'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

function tekenOp(img: HTMLImageElement, w: number, h: number): Beeld {
  const doek = document.createElement('canvas')
  doek.width = Math.max(1, w)
  doek.height = Math.max(1, h)
  const ctx = doek.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('Geen tekenvlak beschikbaar.')
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, 0, 0, doek.width, doek.height)
  const d = ctx.getImageData(0, 0, doek.width, doek.height)
  return { data: d.data, width: d.width, height: d.height }
}

function Voorbeeld({ beeld }: { beeld: Beeld }) {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const c = ref.current
    if (!c) return
    c.width = beeld.width
    c.height = beeld.height
    c.getContext('2d')!.putImageData(new ImageData(beeld.data, beeld.width, beeld.height), 0, 0)
  }, [beeld])
  return <canvas ref={ref} className="scan-doek" />
}

/**
 * De foto met het kader erop. Vier rondjes om te slepen, in de
 * coördinaten van de foto zelf; het scherm rekent om met de getoonde maat.
 * Een vergrootglas zou mooi zijn; groot genoeg (44 px) om te raken is nu de eis.
 */
function HoekenKiezer({ url, bron, hoeken, zet }: {
  url: string; bron: Beeld; hoeken: Hoeken; zet: (h: Hoeken) => void
}) {
  const vlak = useRef<HTMLDivElement>(null)
  const [sleep, setSleep] = useState<number | null>(null)

  function naarFoto(e: React.PointerEvent): { x: number; y: number } | null {
    const r = vlak.current?.getBoundingClientRect()
    if (!r) return null
    return {
      x: Math.min(bron.width - 1, Math.max(0, ((e.clientX - r.left) / r.width) * bron.width)),
      y: Math.min(bron.height - 1, Math.max(0, ((e.clientY - r.top) / r.height) * bron.height)),
    }
  }

  /* De straal van een rondje in fotopixels, zodat hij op het scherm ~22 px is. */
  const schaal = bron.width / (vlak.current?.clientWidth || 360)
  const r = 22 * schaal

  return (
    <div
      ref={vlak}
      className="snij-vlak"
      style={{ aspectRatio: `${bron.width} / ${bron.height}` }}
      onPointerMove={(e) => {
        if (sleep === null) return
        const p = naarFoto(e)
        if (!p) return
        const n = [...hoeken] as Hoeken
        n[sleep] = p
        zet(n)
      }}
      onPointerUp={() => setSleep(null)}
      onPointerCancel={() => setSleep(null)}
    >
      <img src={url} alt="De foto" draggable={false} />
      <svg viewBox={`0 0 ${bron.width} ${bron.height}`} preserveAspectRatio="none">
        <polygon
          points={hoeken.map((p) => `${p.x},${p.y}`).join(' ')}
          fill="rgba(90, 200, 250, 0.18)" stroke="#5ac8fa" strokeWidth={3 * schaal}
        />
        {hoeken.map((p, i) => (
          <circle
            key={i} cx={p.x} cy={p.y} r={r}
            fill={sleep === i ? 'rgba(90,200,250,0.55)' : 'rgba(255,255,255,0.35)'}
            stroke="#5ac8fa" strokeWidth={3 * schaal}
            style={{ touchAction: 'none', cursor: 'grab' }}
            onPointerDown={(e) => {
              e.preventDefault()
              ;(e.currentTarget.ownerSVGElement?.parentElement as HTMLElement | null)?.setPointerCapture(e.pointerId)
              setSleep(i)
            }}
          />
        ))}
      </svg>
    </div>
  )
}
